import ContentLayout from 'components/ContentLayout'
import { ScrollListener, ScrollListenerHandle } from 'components/ScrollListener'
import { ContentItem, Stream } from 'interfaces'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { from, Observable, Observer, Subject } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'
import { getStreamsContent } from 'services/content/streams-content/actions'
import { StoreThunkDispatch } from 'store/state'
import { createStreamTypesMap, StreamsContentState } from 'utils/content'
import { PostDestination } from 'shared'

const BATCH_SIZE_DEFAULT = 2
const CONTENT_FETCH_DEBOUNCE = 100
const STREAMS_UPDATE_DEBOUNCE = 500

export interface StreamsLoaderProps {
  streams: Stream[]
  browseUrlBase: string
  prettyPaths?: boolean
  streamUrlPattern?: string
  batchSize?: number
  hideNavLink?: boolean
  destinations?: { [key: string]: Readonly<PostDestination> }
  streamsOnly?: boolean
  onContentItemClick?(content: ContentItem): void
  onContentItemCompose?(content: ContentItem): void
}

interface StreamsLoaderState {
  loading: boolean
  content: { [key: string]: StreamsContentState }
  page: number
  hasNext: boolean
}

export function StreamsLoader({
  streams,
  browseUrlBase,
  prettyPaths,
  streamUrlPattern,
  batchSize = BATCH_SIZE_DEFAULT,
  hideNavLink,
  destinations,
  streamsOnly = true,
  onContentItemClick,
  onContentItemCompose
}: StreamsLoaderProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()

  const [state, setState] = useState<StreamsLoaderState>({
    loading: false,
    content: {},
    page: 0,
    hasNext: true
  })

  // Track streams synchronously, but key effects/callbacks on stream ids so a
  // new array reference with identical ids does not reset state or refetch
  const streamsRef = useRef(streams)
  streamsRef.current = streams
  const streamsKey = useMemo(() => streams.map(s => s.id).join(','), [streams])

  // Reset state when the set of streams actually changes
  useEffect(() => {
    setState({
      loading: false,
      content: {},
      page: 0,
      hasNext: true
    })
  }, [streamsKey])

  const scrollListenerRef = useRef<ScrollListenerHandle | null>(null)
  const unmountRef = useRef(new Subject<boolean>())
  const hostElementRef = useRef<HTMLDivElement | null>(null)
  const fetchContentSubscriptionRef = useRef<any>(null)
  const mainScrollElementRef = useRef<HTMLDivElement | null>(null)
  // Use ref to track state synchronously for fetch checks
  const stateRef = useRef(state)
  stateRef.current = state

  // Get the main scroll element on mount
  useEffect(() => {
    mainScrollElementRef.current = document.getElementsByTagName('main')[0] as HTMLDivElement
  }, [])

  const fetchStreamsContent = useCallback(
    (streamsToFetch: Stream[]): Promise<StreamsContentState[]> => {
      const streamsContentMap = createStreamTypesMap(streamsToFetch)
      return dispatch(getStreamsContent(streamsContentMap))
        .unwrap()
        .then((response: any) =>
          response.map((data: any) => ({
            stream: streamsToFetch.find(s => s.id === data.id),
            items: data.content,
            loading: false,
            loaded: true
          }))
        )
    },
    [dispatch]
  )

  const onContentFetched = useCallback((streamsContent: StreamsContentState[]) => {
    setState(prevState => {
      const content = { ...prevState.content }
      const hasNext = streamsContent.length === batchSize

      streamsContent.forEach(c => {
        content[c.stream.id] = c
      })

      return {
        ...prevState,
        content,
        loading: false,
        page: prevState.page + 1,
        hasNext
      }
    })

    setTimeout(() => {
      const scrollListener = scrollListenerRef.current
      const hostElement = hostElementRef.current
      const mainElement = mainScrollElementRef.current

      if (
        scrollListener
        && hostElement
        && mainElement
        && (
          hostElement.clientHeight < mainElement.clientHeight
          || mainElement.scrollHeight - mainElement.scrollTop === mainElement.clientHeight
        )
      ) {
        scrollListener.emitScroll()
      }
    }, 0)
  }, [batchSize])

  const fetchContentNextPage = useCallback(() => {
    const currentState = stateRef.current
    if (currentState.loading || !currentState.hasNext) {
      return
    }

    const streams = streamsRef.current
    const startIndex = currentState.page * batchSize
    const nextStreams = streams.slice(startIndex, startIndex + batchSize)

    if (streams.length !== 0 && nextStreams.length === 0) {
      setState(prevState => ({ ...prevState, hasNext: false }))
      return
    }

    if (nextStreams.length === 0) {
      return
    }

    const nextStreamIds = nextStreams.map(s => s.id)
    setState(prevState => {
      const content = { ...prevState.content }
      Object.keys(prevState.content)
        .filter(key => nextStreamIds.indexOf(key) !== -1)
        .forEach(id => {
          content[id] = { ...prevState.content[id], loading: true }
        })
      return { ...prevState, loading: true, content }
    })

    if (streamsOnly) {
      fetchContentSubscriptionRef.current = new Observable(
        (observer: Observer<StreamsContentState[]>) =>
          observer.next(
            nextStreams.map(stream => ({
              stream,
              items: [],
              loading: false,
              loaded: true
            }))
          )
      )
        .pipe(
          debounceTime(STREAMS_UPDATE_DEBOUNCE)
        )
        .subscribe(onContentFetched)
    } else {
      fetchContentSubscriptionRef.current = from(
        fetchStreamsContent(nextStreams)
      ).pipe(
        debounceTime(CONTENT_FETCH_DEBOUNCE),
        takeUntil(unmountRef.current.asObservable())
      )
        .subscribe(onContentFetched)
    }
    // streamsKey is intentional: recreating this callback on a real streams
    // change is what refires the fetch effect below to load the new streams
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamsKey, batchSize, streamsOnly, fetchStreamsContent, onContentFetched])

  // Fetch content on mount and when streams change
  useEffect(() => {
    fetchContentNextPage()
  }, [fetchContentNextPage])

  // Cleanup on unmount
  useEffect(() => {
    const unmount$ = unmountRef.current

    return () => {
      unmount$.next(true)
      if (fetchContentSubscriptionRef.current) {
        fetchContentSubscriptionRef.current.unsubscribe()
      }
    }
  }, [])

  const filteredContent = useMemo(() => {
    return streamsRef.current
      .map(stream => state.content[stream.id])
      .filter(Boolean)
      .filter(content => content.loaded || content.loading)
    // streamsKey stands in for streamsRef.current (same ids => same result)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamsKey, state.content])

  return (
    <ScrollListener
      emitTreshold={350}
      scrollElement={mainScrollElementRef.current as HTMLElement}
      onScroll={fetchContentNextPage}
      ref={scrollListenerRef}
    >
      <div ref={hostElementRef} data-testid="streams-loader">
        {filteredContent.map(content => {
          let browseUrl = prettyPaths
            ? `${browseUrlBase}/${content.stream.slug}`
            : `${browseUrlBase}/${content.stream.id}`

          const loading = streamsOnly ? undefined : content.loading
          const items = streamsOnly ? undefined : content.items

          if (streamUrlPattern) {
            const streamUrl = streamUrlPattern
              .split('-')
              .map(prop => (content.stream as any)[prop] || '')
              .filter(Boolean)
              .join('-')
            browseUrl = `${browseUrlBase}/${streamUrl}`
          }

          return (
            <ContentLayout
              key={`stream-${content.stream.id}`}
              source={content.stream}
              loading={loading}
              items={items}
              hideNavLink={hideNavLink}
              destinations={destinations}
              browseUrl={browseUrl}
              onContentItemClick={onContentItemClick}
              onContentItemCompose={onContentItemCompose}
            />
          )
        })}
      </div>
    </ScrollListener>
  )
}

export default StreamsLoader
